import { createDocument } from './firebase';
import { placeIdLookup } from '../utils/placeIdLookup';

import type { OutletAddressFields } from './geocoding';

type SampleSource = 'AddressValidator' | 'AddOutlet' | 'Other';

interface AddressComponentLite {
  long_name: string;
  short_name?: string;
  types: string[];
}

interface AddressSamplePayload {
  source: SampleSource | string;
  coordinates: { lat: number; lng: number };
  formattedAddress?: string;
  addressComponents: AddressComponentLite[];
  parserOutput?: OutletAddressFields;
  detected: {
    city: string | null;
    sublocality_level_1: string | null;
    sublocality_level_2: string | null;
    sublocality_level_3: string | null;
    neighborhood?: string | null;
  };
  placeIds?: {
    area_sublocality_level_1?: string | null;
    locality_sublocality_level_2?: string | null;
    locality_sublocality_level_3?: string | null;
    locality_neighborhood?: string | null;
  };
  createdAt: Date;
}

function extractFirstByType(components: any[], type: string): string | undefined {
  for (const comp of components) {
    if (Array.isArray(comp.types) && comp.types.includes(type)) {
      return comp.long_name as string;
    }
  }
  return undefined;
}

function extractCity(components: any[]): string | undefined {
  // Prefer locality, fallback to administrative_area_level_2
  return (
    extractFirstByType(components, 'locality') ||
    extractFirstByType(components, 'administrative_area_level_2') ||
    undefined
  );
}

export async function logAddressComponentsSample(
  source: SampleSource | string,
  lat: number,
  lng: number,
  rawGeocodeResult: any,
  parserOutput?: OutletAddressFields
): Promise<void> {
  try {
    const components: any[] = rawGeocodeResult?.address_components || [];
    const formattedAddress: string | undefined = rawGeocodeResult?.formatted_address;

    const sub1 = extractFirstByType(components, 'sublocality_level_1');
    const sub2 = extractFirstByType(components, 'sublocality_level_2');
    const sub3 = extractFirstByType(components, 'sublocality_level_3');
    const neighborhood = extractFirstByType(components, 'neighborhood');
    const city = extractCity(components);

    // Attempt placeId lookups if Places API is available
  let areaPlaceId: string | null = null;
  let locality2PlaceId: string | null = null;
  let locality3PlaceId: string | null = null;
  let neighborhoodPlaceId: string | null = null;

    if (placeIdLookup.isAvailable()) {
      const lookups: Array<Promise<void>> = [];

      if (sub1) {
        lookups.push(
          (async () => {
            areaPlaceId = await placeIdLookup.getAreaPlaceId(sub1, city);
          })()
        );
      }
      if (sub2) {
        lookups.push(
          (async () => {
            locality2PlaceId = await placeIdLookup.getLocalityPlaceId(sub2, sub1, city);
          })()
        );
      }
      if (sub3) {
        lookups.push(
          (async () => {
            locality3PlaceId = await placeIdLookup.getLocalityPlaceId(sub3, sub1 || sub2, city);
          })()
        );
      }
      if (neighborhood) {
        lookups.push(
          (async () => {
            neighborhoodPlaceId = await placeIdLookup.getNeighborhoodPlaceId(neighborhood, sub1 || sub2, city);
          })()
        );
      }

      if (lookups.length) {
        await Promise.allSettled(lookups);
      }
    }

    const addressComponents: AddressComponentLite[] = components.map((c) => {
      const item: any = {
        long_name: String(c?.long_name ?? ''),
        types: Array.isArray(c?.types) ? c.types.filter(Boolean) : [],
      };
      if (c?.short_name !== undefined) {
        item.short_name = c.short_name;
      }
      return item as AddressComponentLite;
    });

    const payload: any = {
      source,
      coordinates: { lat, lng },
      addressComponents,
      parserOutput,
      detected: {
        city: city ?? null,
        sublocality_level_1: sub1 ?? null,
        sublocality_level_2: sub2 ?? null,
        sublocality_level_3: sub3 ?? null,
        neighborhood: neighborhood ?? null,
      },
      placeIds: {
        area_sublocality_level_1: areaPlaceId ?? null,
        locality_sublocality_level_2: locality2PlaceId ?? null,
        locality_sublocality_level_3: locality3PlaceId ?? null,
        locality_neighborhood: neighborhoodPlaceId ?? null,
      },
      createdAt: new Date(),
    };

    if (formattedAddress !== undefined) {
      payload.formattedAddress = formattedAddress;
    }

    await createDocument('address_samples', payload as AddressSamplePayload);
  } catch (err) {
    console.warn('Failed to log address components sample:', err);
  }
}
